import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, saas } from '../supabaseClient';
import { Star, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { getCurrentRestaurantBrand, getPublicRestaurantBrand } from '../services/restaurantBrand';

const UserFeedback = () => {
  const [searchParams] = useSearchParams();
  const orderIdFromUrl = searchParams.get('id');

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    rating: 0,
    message: '',
    order_id: orderIdFromUrl || ''
  });
  const [hoveredRating, setHoveredRating] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [brand, setBrand] = useState({
    displayName: 'Restaurant',
    branchName: '',
    logoUrl: '',
  });

  useEffect(() => {
    if (orderIdFromUrl) {
      const fetchOrderDetails = async () => {
        try {
          const { data, error } = await saas('orders')
            .select(`
              restaurant_id,
              customer_number,
              customers (
                name
              )
            `)
            .eq('order_id', orderIdFromUrl)
            .maybeSingle();

          if (error) throw error;

          if (data) {
            setFormData(prev => ({
              ...prev,
              name: data.customers?.name || '',
              phone: data.customer_number || '',
              order_id: orderIdFromUrl
            }));
            if (data.restaurant_id) {
              const nextBrand = await getPublicRestaurantBrand(data.restaurant_id);
              setBrand(nextBrand);
            }
          }
        } catch (err) {
          console.error("Error fetching order details:", err);
        }
      };
      fetchOrderDetails();
    } else {
      getCurrentRestaurantBrand()
        .then(setBrand)
        .catch((err) => console.warn('Feedback brand load failed:', err));
    }
  }, [orderIdFromUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.rating === 0) {
      alert("Please select a rating!");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: insertError } = await supabase
        .from('customer_feedback')
        .insert([{
          customer_name: formData.name,
          mobile_number: formData.phone,
          rating: formData.rating,
          message: formData.message,
          order_id: formData.order_id || null
        }]);

      if (insertError) throw insertError;
      setSubmitted(true);
    } catch (err) {
      console.error("Feedback error:", err);
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-6 font-space">
        <div className="w-full max-w-md bg-[#1a1a1a] border border-[#333] rounded-[40px] p-10 text-center shadow-2xl animate-in zoom-in-95">
          {brand.logoUrl && (
            <img src={brand.logoUrl} alt={brand.displayName} className="mx-auto mb-5 h-16 w-16 rounded-2xl object-contain border border-[#f7600c]/30 bg-black/20" />
          )}
          <div className="w-20 h-20 bg-emerald-500 text-white rounded-[25px] flex items-center justify-center mx-auto mb-6 shadow-lg shadow-emerald-500/20">
            <CheckCircle2 size={40} />
          </div>
          <h2 className="text-3xl font-black italic uppercase tracking-tighter text-white mb-2">Thank You!</h2>
          <p className="text-gray-400 font-bold uppercase text-[10px] tracking-[0.2em] mb-8">Your feedback helps us improve</p>
          <button
            onClick={() => setSubmitted(false)}
            className="w-full py-4 bg-[#f7600c] text-white rounded-2xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all"
          >
            Submit Another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center p-4 font-space">
      <div className="w-full max-w-md glass-card rounded-[3rem] overflow-hidden flex flex-col shadow-2xl border border-[#333] bg-[#0d0d0d]">
        <div className="p-8 border-b border-[#333] bg-[#111] text-center">
          {brand.logoUrl && (
            <img src={brand.logoUrl} alt={brand.displayName} className="mx-auto mb-4 h-16 w-16 rounded-2xl object-contain border border-[#f7600c]/30 bg-black/20" />
          )}
          <h1 className="text-2xl font-black tracking-tighter text-[#f7600c] uppercase italic leading-none">{brand.displayName}</h1>
          {brand.branchName && (
            <p className="mt-2 text-[9px] font-bold text-gray-600 uppercase tracking-widest">{brand.branchName}</p>
          )}
          <p className="text-[9px] font-bold text-gray-500 uppercase tracking-widest mt-2">Customer Feedback Form</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-6">
          <div className="space-y-4">
            <div>
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Full Name</label>
              <input
                required
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full bg-[#0d0d0d] border border-[#333] rounded-2xl px-5 py-4 text-white focus:border-[#f7600c] transition-all outline-none"
                placeholder="Enter your name"
              />
            </div>

            <div>
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Mobile Number</label>
              <input
                required
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full bg-[#0d0d0d] border border-[#333] rounded-2xl px-5 py-4 text-white focus:border-[#f7600c] transition-all outline-none"
                placeholder="03xx xxxxxxx"
              />
            </div>

            <div className="text-center py-2">
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-3 block">Overall Rating</label>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onMouseEnter={() => setHoveredRating(star)}
                    onMouseLeave={() => setHoveredRating(0)}
                    onClick={() => setFormData({ ...formData, rating: star })}
                    className="transition-transform active:scale-90"
                  >
                    <Star
                      size={36}
                      className={`transition-colors ${(hoveredRating || formData.rating) >= star
                        ? 'fill-[#f7600c] text-[#f7600c]'
                        : 'text-[#333]'
                        }`}
                    />
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[9px] font-black text-gray-500 uppercase tracking-widest mb-1.5 block">Your Message</label>
              <textarea
                rows="4"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full bg-[#0d0d0d] border border-[#333] rounded-2xl px-5 py-4 text-white focus:border-[#f7600c] transition-all outline-none resize-none"
                placeholder="Tell us about your experience..."
              ></textarea>
            </div>
          </div>

          {error && <p className="text-red-500 text-[10px] font-bold uppercase text-center">{error}</p>}

          <button
            disabled={loading}
            type="submit"
            className="w-full py-5 bg-[#f7600c] text-white rounded-3xl font-black uppercase tracking-widest text-xs shadow-xl active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin" /> : <><Send size={18} /> Submit Feedback</>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserFeedback;
